import React from "react";
import PropTypes from "prop-types";
import {
  getClaimsByInstitutionOutcome,
  getClaimsByInstitutionOutcomeAndGround,
  getClaimsByOutcomeAtInstitution,
  getClaimsByOutcomeAndGroundAtInstitution,
  getClaimsByPetitionerLawfirmInstitutionOutcome,
  getClaimsByPatentOwnerLawfirmInstitutionOutcome,
  getClaimsByAuthorJudgeInstitutionOutcome,
  getClaimsByPanelJudgeInstitutionOutcome,
  getClaimsByPetitionerLawfirmFWDOutcome,
  getClaimsByPatentOwnerLawfirmFWDOutcome,
  getClaimsByAuthorJudgeFWDOutcome,
  getClaimsByPanelJudgeFWDOutcome
} from "../../../../services/api-ptab";
import { DonutChart, StackedBarChart, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { nestedObjectsToGroupedSeries, objectToSeries, orderBy } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import { withChartData } from "../../../common/chart-data-hoc";
import { withChartConfig } from "../../../common/chart-config-hoc";

const COLOR_MAP = {
  Instituted: Colors.blue400,
  Unpatentable: Colors.blue400,
  Cancelled: Colors.blue300,
  Settled: Colors.blue100,
  Patentable: Colors.grey500,
  Denied: Colors.grey400,
  "Not Instituted": Colors.grey400,
  Pending: Colors.orange300
};

const MARGIN_LEFT = { left: 160, right: 50, top: 50, bottom: 50 };
const MARGIN_MORE_LEFT = { left: 280, right: 50, top: 50, bottom: 50 };

const propValidation = { query: PropTypes.object };

export const ClaimsByInstitutionOutcomeChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["Denied", "Instituted"]);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getClaimsByInstitutionOutcome, props.query));
  return <Chart title="Institutions for Challenged Claims" dataFormatter={dataFormatter} colorMap={COLOR_MAP} scrollable={false} {...props} />;
};
ClaimsByInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByGroundsAndInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByInstitutionOutcomeAndGround, props.query), {
    show: "percentage",
    sort: ["102", "102 & 103", "103"]
  });
  return (
    <Chart
      dataFormatter={dataFormatter}
      title="Institutions for Challenged Claims"
      subtitle="by Invalidity (102 & 103) Grounds"
      axisTitleX="Petitioner Asserted Grounds"
      colorMap={COLOR_MAP}
      tickTotalY={5}
      legends={["Denied", "Instituted"]}
      scrollable={false}
      {...props}
    />
  );
};
ClaimsByGroundsAndInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByOutcomeAtInstitutionChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["Patentable", "Pending", "Settled", "Cancelled", "Unpatentable"]);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getClaimsByOutcomeAtInstitution, props.query));
  return <Chart title="Outcomes for Instituted Claims" dataFormatter={dataFormatter} colorMap={COLOR_MAP} scrollable={false} {...props} />;
};
ClaimsByOutcomeAtInstitutionChart.propTypes = propValidation;

export const ClaimsByGroundsAndOutcomeAtInstitutionChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByOutcomeAndGroundAtInstitution, props.query), {
    show: "percentage",
    sort: ["102", "102 & ~103", "102 & 103", "~102 & 103", "103"]
  });
  return (
    <Chart
      dataFormatter={dataFormatter}
      title="Outcomes for Instituted Claims"
      subtitle="by Invalidity (102 & 103) Grounds"
      axisTitleX="Board Instituted Grounds"
      colorMap={COLOR_MAP}
      tickTotalY={5}
      legends={["Patentable", "Pending", "Settled", "Cancelled", "Unpatentable"]}
      scrollable={false}
      {...props}
    />
  );
};
ClaimsByGroundsAndOutcomeAtInstitutionChart.propTypes = propValidation;

export const ClaimsByPetitionerLawfirmInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByPetitionerLawfirmInstitutionOutcome, props.query), {
    show: "count",
    sort: "Instituted"
  });
  return (
    <Chart
      title="Institution Rates for Petitioners' Counsel"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Instituted", "Denied"]}
      {...props}
    />
  );
};
ClaimsByPetitionerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByPatentOwnerLawfirmInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByPatentOwnerLawfirmInstitutionOutcome, props.query), {
    show: "count",
    sort: "Denied"
  });
  return (
    <Chart
      title="Institution Rates for Patent Owners' Counsel"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Instituted", "Denied"]}
      {...props}
    />
  );
};
ClaimsByPatentOwnerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByAuthorJudgeInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByAuthorJudgeInstitutionOutcome, props.query), {
    show: "count",
    sort: "__total__"
  });
  return (
    <Chart
      title="Institution Rates for Author Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["Instituted", "Denied"]}
      {...props}
    />
  );
};
ClaimsByAuthorJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByPanelJudgeInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByPanelJudgeInstitutionOutcome, props.query), {
    show: "count",
    sort: "__total__"
  });
  return (
    <Chart
      title="Institution Rates for Panel Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["Instituted", "Denied"]}
      {...props}
    />
  );
};
ClaimsByPanelJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const ClaimsByPetitionerLawfirmFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByPetitionerLawfirmFWDOutcome, props.query), {
    show: "count",
    sort: "Unpatentable"
  });
  return (
    <Chart
      dataFormatter={dataFormatter}
      title="Final Written Decision Rates for Petitioners' Counsel"
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
      {...props}
    />
  );
};
ClaimsByPetitionerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const ClaimsByPatentOwnerLawfirmFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByPatentOwnerLawfirmFWDOutcome, props.query), {
    show: "count",
    sort: "Patentable"
  });
  return (
    <Chart
      dataFormatter={dataFormatter}
      title="Final Written Decision Rates for Patent Owners' Counsel"
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
      {...props}
    />
  );
};
ClaimsByPatentOwnerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const ClaimsByAuthorJudgeFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByAuthorJudgeFWDOutcome, props.query), {
    show: "count",
    sort: "__total__"
  });
  return (
    <Chart
      title="Final Written Decision Rates for Author Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
      {...props}
    />
  );
};
ClaimsByAuthorJudgeFWDOutcomeChart.propTypes = propValidation;

export const ClaimsByPanelJudgeFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getClaimsByPanelJudgeFWDOutcome, props.query), {
    show: "count",
    sort: "__total__"
  });
  return (
    <Chart
      title="Final Written Decision Rates for Panel Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["Patentable", "Unpatentable", "Cancelled", "Settled", "Pending"]}
      {...props}
    />
  );
};
ClaimsByPanelJudgeFWDOutcomeChart.propTypes = propValidation;
